import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, PackageCheck, Truck, Boxes, Eye, Trash2, RotateCcw, UserCog, FileText, Search,
} from "lucide-react";

const ESTADO_STYLE = {
  pendiente: "bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]",
  empacado: "bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]",
  despachado: "bg-[#eef1f7] text-[#2b3f63] border border-[#b9c4dc]",
};

const facturaSinAutorizar = (it) => it.documento_tipo === "factura" && it.documento_estado_sri && !["autorizado", "historico"].includes(it.documento_estado_sri);

function SriPendienteBadge({ it }) {
  if (!facturaSinAutorizar(it)) return null;
  return (
    <Badge className="bg-[#fff8e6] text-[#8a5a00] border border-[#e8c98a] text-[10px] mt-1" title="Puede armarse y empacarse; para despachar la factura debe estar autorizada por el SRI" data-testid={`sri-pendiente-${it.numero}`}>
      Factura {it.documento_estado_sri} SRI · sin autorizar
    </Badge>
  );
}

export default function Despacho() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("pendiente");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [empacar, setEmpacar] = useState(null);
  const [despachar, setDespachar] = useState(null);
  const [guia, setGuia] = useState(null);

  const esAdmin = ["admin", "gerencia"].includes((user?.role || "").toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: u }] = await Promise.all([
        api.get("/despacho"),
        api.get("/users/basic").catch(() => ({ data: [] })),
      ]);
      setItems(data || []);
      setUsers(u || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    pendiente: items.filter(i => i.estado === "pendiente").length,
    empacado: items.filter(i => i.estado === "empacado").length,
    despachado: items.filter(i => i.estado === "despachado").length,
  }), [items]);

  const visibles = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter(i => i.estado === tab).filter(i => !s || [
      i.numero, i.documento_numero, i.cliente_nombre, i.asignado_a_nombre, i.guia_remision_numero,
    ].some(v => (v || "").toString().toLowerCase().includes(s)));
  }, [items, tab, q]);

  const asignar = async (it, usuarioId) => {
    try {
      await api.patch(`/despacho/${it.id}/asignar`, { asignado_a: usuarioId || null });
      toast.success("Responsable actualizado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reabrir = async (it) => {
    if (!window.confirm(`¿Reabrir ${it.numero} y volverlo a "Pendiente"?`)) return;
    try { await api.patch(`/despacho/${it.id}/reabrir`); toast.success("Despacho reabierto"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const eliminar = async (it) => {
    if (!window.confirm(`¿Quitar ${it.numero} de la cola de despacho?`)) return;
    try { await api.delete(`/despacho/${it.id}`); toast.success("Eliminado de la cola"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="despacho-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Bodega</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Despacho de pedidos</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Cola de armado: notas de pedido y facturas (las que están en borrador SRI se pueden armar y empacar; se despachan solo cuando el SRI las autoriza), con conteo de cartones para la guía de remisión.</p>
        </div>
        <Button onClick={() => setNuevoOpen(true)} className="bg-[#205c45]" data-testid="despacho-nuevo-btn">
          <Plus size={16} className="mr-2" />Enviar documento a despacho
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="pendiente" data-testid="tab-pendiente">Pendientes ({counts.pendiente})</TabsTrigger>
          <TabsTrigger value="empacado" data-testid="tab-empacado">Empacados ({counts.empacado})</TabsTrigger>
          <TabsTrigger value="despachado" data-testid="tab-despachado">Despachados ({counts.despachado})</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="relative w-full md:w-96">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8ba397]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8"
          placeholder="Buscar (DSP, documento, cliente, responsable)…" data-testid="despacho-search" />
      </div>

      {/* ESCRITORIO */}
      <div className="hidden md:block rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead>
            <tr>
              <th>Despacho</th>
              <th>Documento</th>
              <th>Cliente</th>
              <th>Responsable</th>
              <th className="text-center">Ítems</th>
              <th className="text-center">Cartones</th>
              <th>Estado</th>
              <th className="w-64"></th>
            </tr>
          </thead>
          <tbody>
            {visibles.map(it => (
              <tr key={it.id} data-testid={`despacho-row-${it.numero}`}>
                <td className="font-mono text-xs">
                  {it.numero}
                  {it.prioridad === "urgente" && <Badge className="ml-2 bg-[#fdecea] text-[#7a2514] border border-[#d99584] text-[10px]">URGENTE</Badge>}
                </td>
                <td className="text-xs">
                  <div className="font-mono">{it.documento_numero || "—"}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">
                    {it.documento_tipo === "factura" ? "Factura" : "Nota de pedido"} · {it.documento_fecha || ""}
                  </div>
                </td>
                <td className="text-xs">{it.cliente_nombre || "—"}</td>
                <td className="text-xs">
                  <Select value={it.asignado_a || "__none__"} onValueChange={(v) => asignar(it, v === "__none__" ? "" : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`asignar-${it.numero}`}>
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin asignar</SelectItem>
                      {users.filter(u => u.activo !== false).map(u => (
                        <SelectItem key={u.id} value={u.id}>{`${u.nombres || ""} ${u.apellidos || ""}`.trim() || u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="text-center tabular-nums">{(it.lineas || []).length}</td>
                <td className="text-center tabular-nums font-semibold">{it.cartones ?? "—"}</td>
                <td><Badge className={`capitalize ${ESTADO_STYLE[it.estado] || ""}`}>{it.estado}</Badge>
                  <SriPendienteBadge it={it} />
                  {it.estado === "despachado" && it.fecha_despacho && (
                    <div className="text-[10px] text-[#5c7368] mt-0.5">Salida: {it.fecha_despacho}</div>
                  )}
                </td>
                <td>
                  <div className="flex flex-wrap gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setDetalle(it)} data-testid={`ver-${it.numero}`}>
                      <Eye size={13} className="mr-1" />Ver
                    </Button>
                    {it.estado !== "despachado" && (
                      <Button size="sm" variant="outline" onClick={() => setEmpacar({ ...it, cartones_input: it.cartones || 1 })} data-testid={`empacar-${it.numero}`}>
                        <Boxes size={13} className="mr-1" />{it.estado === "empacado" ? "Editar cartones" : "Empacar"}
                      </Button>
                    )}
                    {it.estado === "empacado" && (
                      <>
                        <Button size="sm" className="bg-[#205c45]" onClick={() => setGuia(it)} disabled={facturaSinAutorizar(it)} data-testid={`guia-${it.numero}`}>
                          <FileText size={13} className="mr-1" />Guía
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDespachar(it)} disabled={facturaSinAutorizar(it)} title={facturaSinAutorizar(it) ? "La factura debe estar autorizada por el SRI para despachar" : undefined} data-testid={`despachar-${it.numero}`}>
                          <Truck size={13} className="mr-1" />Despachar
                        </Button>
                      </>
                    )}
                    {it.estado !== "pendiente" && (
                      <Button size="sm" variant="ghost" onClick={() => reabrir(it)} title="Reabrir" data-testid={`reabrir-${it.numero}`}>
                        <RotateCcw size={13} />
                      </Button>
                    )}
                    {esAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => eliminar(it)} title="Quitar de la cola" data-testid={`eliminar-${it.numero}`}>
                        <Trash2 size={13} className="text-[#7a2514]" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {visibles.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center py-10 text-[#8ba397] text-sm">
                Sin documentos en <strong>{tab}</strong>. Use “Enviar documento a despacho” para armar la cola.
              </td></tr>
            )}
            {loading && <tr><td colSpan={8} className="text-center py-6 text-[#8ba397]">Cargando…</td></tr>}
          </tbody>
        </table>
      </div>

      {/* MÓVIL: tarjetas apiladas, sin desplazamiento horizontal */}
      <div className="space-y-3 md:hidden" data-testid="despacho-cards-mobile">
        {visibles.map(it => (
          <div key={it.id} className="rounded-lg border border-[#d1dbd6] bg-white p-3 space-y-2"
            data-testid={`despacho-card-${it.numero}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-xs text-[#5c7368]">{it.numero}</div>
                <div className="font-semibold text-sm text-[#15241d] truncate">{it.cliente_nombre || "—"}</div>
                <div className="text-[11px] text-[#5c7368]">
                  {it.documento_tipo === "factura" ? "Factura" : "Nota de pedido"} {it.documento_numero || "—"}
                  {it.documento_fecha ? ` · ${it.documento_fecha}` : ""}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <Badge className={`capitalize ${ESTADO_STYLE[it.estado] || ""}`}>{it.estado}</Badge>
                <SriPendienteBadge it={it} />
                {it.prioridad === "urgente" && (
                  <Badge className="bg-[#fdecea] text-[#7a2514] border border-[#d99584] text-[10px]">URGENTE</Badge>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded bg-[#f7f9f8] border border-[#d1dbd6] py-1">
                <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Ítems</div>
                <div className="font-semibold tabular-nums">{(it.lineas || []).length}</div>
              </div>
              <div className="rounded bg-[#f7f9f8] border border-[#d1dbd6] py-1">
                <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Cartones</div>
                <div className="font-semibold tabular-nums">{it.cartones ?? "—"}</div>
              </div>
              <div className="rounded bg-[#f7f9f8] border border-[#d1dbd6] py-1">
                <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Salida</div>
                <div className="font-semibold">{it.fecha_despacho || "—"}</div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#5c7368] mb-1">Responsable</div>
              <Select value={it.asignado_a || "__none__"} onValueChange={(v) => asignar(it, v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs" data-testid={`asignar-mobile-${it.numero}`}>
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {users.filter(u => u.activo !== false).map(u => (
                    <SelectItem key={u.id} value={u.id}>{`${u.nombres || ""} ${u.apellidos || ""}`.trim() || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" className="h-10" onClick={() => setDetalle(it)}
                data-testid={`ver-mobile-${it.numero}`}>
                <Eye size={14} className="mr-1" />Ver productos
              </Button>
              {it.estado !== "despachado" && (
                <Button size="sm" variant="outline" className="h-10"
                  onClick={() => setEmpacar({ ...it, cartones_input: it.cartones || 1 })}
                  data-testid={`empacar-mobile-${it.numero}`}>
                  <Boxes size={14} className="mr-1" />{it.estado === "empacado" ? "Cartones" : "Empacar"}
                </Button>
              )}
              {it.estado === "empacado" && (
                <>
                  <Button size="sm" className="bg-[#205c45] h-10" onClick={() => setGuia(it)} disabled={facturaSinAutorizar(it)}
                    data-testid={`guia-mobile-${it.numero}`}>
                    <FileText size={14} className="mr-1" />Guía
                  </Button>
                  <Button size="sm" variant="outline" className="h-10" onClick={() => setDespachar(it)} disabled={facturaSinAutorizar(it)}
                    data-testid={`despachar-mobile-${it.numero}`}>
                    <Truck size={14} className="mr-1" />Despachar
                  </Button>
                </>
              )}
              {it.estado !== "pendiente" && (
                <Button size="sm" variant="ghost" className="h-10" onClick={() => reabrir(it)}
                  data-testid={`reabrir-mobile-${it.numero}`}>
                  <RotateCcw size={14} className="mr-1" />Reabrir
                </Button>
              )}
              {esAdmin && (
                <Button size="sm" variant="ghost" className="h-10 text-[#7a2514]" onClick={() => eliminar(it)}
                  data-testid={`eliminar-mobile-${it.numero}`}>
                  <Trash2 size={14} className="mr-1" />Quitar
                </Button>
              )}
            </div>
          </div>
        ))}
        {visibles.length === 0 && !loading && (
          <div className="rounded-lg border-2 border-dashed border-[#d1dbd6] py-10 text-center text-sm text-[#8ba397]">
            Sin documentos en <strong>{tab}</strong>.
          </div>
        )}
        {loading && <div className="py-6 text-center text-sm text-[#8ba397]">Cargando…</div>}
      </div>

      {nuevoOpen && <NuevoDespachoDialog users={users} onClose={() => setNuevoOpen(false)} onSaved={() => { setNuevoOpen(false); load(); }} />}
      {detalle && <DetalleDialog item={detalle} onClose={() => setDetalle(null)} />}
      {empacar && <EmpacarDialog item={empacar} onClose={() => setEmpacar(null)} onSaved={() => { setEmpacar(null); load(); }} />}
      {despachar && <DespacharDialog item={despachar} onClose={() => setDespachar(null)} onSaved={() => { setDespachar(null); load(); }} />}
      {guia && <GuiaDialog item={guia} onClose={() => setGuia(null)} onSaved={() => { setGuia(null); load(); }} />}
    </div>
  );
}

function NuevoDespachoDialog({ users, onClose, onSaved }) {
  const [tipo, setTipo] = useState("nota_pedido");
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [prioridad, setPrioridad] = useState("normal");
  const [asignado, setAsignado] = useState("");
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSel(null);
    api.get(`/despacho/documentos-disponibles?tipo=${tipo}`)
      .then(r => setDocs(r.data || []))
      .catch(e => toast.error(formatApiError(e)));
  }, [tipo]);

  const visibles = useMemo(() => {
    const s = q.trim().toLowerCase();
    return docs.filter(d => !s || [d.numero, d.cliente_nombre, d.vendedor_nombre].some(v => (v || "").toLowerCase().includes(s))).slice(0, 60);
  }, [docs, q]);

  const guardar = async () => {
    if (!sel) return toast.error("Seleccione un documento");
    setSaving(true);
    try {
      const { data } = await api.post("/despacho", {
        documento_tipo: tipo, documento_id: sel.id, prioridad,
        asignado_a: asignado || null, observaciones: obs || null,
      });
      toast.success(`${data.numero} enviado a despacho`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Enviar documento a despacho</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>Tipo de documento</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="nuevo-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nota_pedido">Nota de pedido</SelectItem>
                <SelectItem value="factura">Factura</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Prioridad</Label>
            <Select value={prioridad} onValueChange={setPrioridad}>
              <SelectTrigger data-testid="nuevo-prioridad"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="urgente">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Asignar a</Label>
            <Select value={asignado || "__none__"} onValueChange={(v) => setAsignado(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="nuevo-asignado"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sin asignar</SelectItem>
                {users.filter(u => u.activo !== false).map(u => (
                  <SelectItem key={u.id} value={u.id}>{`${u.nombres || ""} ${u.apellidos || ""}`.trim() || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar documento por número, cliente o vendedor…" data-testid="nuevo-buscar" />
        </div>
        <div className="border border-[#d1dbd6] rounded-md max-h-72 overflow-auto">
          <table className="erp-table">
            <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th className="text-center">Ítems</th><th className="text-right">Total</th><th>Estado</th></tr></thead>
            <tbody>
              {visibles.map(d => (
                <tr key={d.id}
                  onClick={() => setSel(d)}
                  className={`cursor-pointer ${sel?.id === d.id ? "bg-[#e9f2ee]" : "hover:bg-[#fafafa]"}`}
                  data-testid={`doc-opt-${d.numero}`}>
                  <td className="font-mono text-xs">{d.numero}</td>
                  <td className="text-xs">{d.fecha}</td>
                  <td className="text-xs">{d.cliente_nombre || "—"}</td>
                  <td className="text-center tabular-nums text-xs">{d.n_lineas}</td>
                  <td className="text-right tabular-nums text-xs">${(d.total || 0).toFixed(2)}</td>
                  <td className="text-xs capitalize">{d.estado === "borrador" || d.estado === "firmado"
                    ? <Badge className="bg-[#fff8e6] text-[#8a5a00] border border-[#e8c98a] text-[10px] normal-case">{d.estado} SRI</Badge>
                    : (d.estado || "—")}</td>
                </tr>
              ))}
              {visibles.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-xs text-[#8ba397]">Sin documentos disponibles (los que ya están en la cola no se listan)</td></tr>}
            </tbody>
          </table>
        </div>

        <div>
          <Label>Observaciones para bodega</Label>
          <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} placeholder="Ej: entregar antes de las 11h00, embalaje con gel refrigerante…" data-testid="nuevo-obs" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-[#205c45]" data-testid="nuevo-guardar">
            {saving ? "Enviando…" : "Enviar a despacho"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetalleDialog({ item, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {item.numero} · {item.documento_tipo === "factura" ? "Factura" : "Nota de pedido"} {item.documento_numero}
          </DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-4 gap-3 text-xs bg-[#f7f9f8] border border-[#d1dbd6] rounded-md p-3">
          <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Cliente</div><div className="font-semibold">{item.cliente_nombre || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Dirección</div><div>{item.cliente_direccion || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Teléfono</div><div>{item.cliente_telefono || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Vendedor</div><div>{item.vendedor_nombre || "—"}</div></div>
        </div>
        {(item.observaciones || item.observaciones_documento || item.observaciones_bodega || item.notas_despacho || item.fecha_despacho) && (
          <div className="text-xs space-y-1 border border-[#f5c477] bg-[#fef7ec] rounded-md p-3">
            {item.observaciones_documento && <div><strong>Observaciones del documento:</strong> {item.observaciones_documento}</div>}
            {item.observaciones && <div><strong>Instrucciones de despacho:</strong> {item.observaciones}</div>}
            {item.observaciones_bodega && <div><strong>Notas de bodega:</strong> {item.observaciones_bodega}</div>}
            {item.notas_despacho && <div><strong>Notas del despacho:</strong> {item.notas_despacho}</div>}
            {item.fecha_despacho && <div><strong>Fecha de despacho:</strong> {item.fecha_despacho}{item.transportista ? ` · ${item.transportista}` : ""}</div>}
          </div>
        )}
        {(item.informacion_adicional || []).length > 0 && (
          <div className="text-xs border border-[#d1dbd6] rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#5c7368] mb-1">Información adicional</div>
            {item.informacion_adicional.map((ia, i) => <div key={i}>{ia.label}: <strong>{ia.valor}</strong></div>)}
          </div>
        )}
        {/* MÓVIL: cada producto en una tarjeta vertical */}
        <div className="space-y-2 md:hidden max-h-[55vh] overflow-y-auto" data-testid="detalle-lineas-mobile">
          {(item.lineas || []).map((l, i) => (
            <div key={i} className="rounded-lg border border-[#d1dbd6] bg-white p-3 space-y-1"
              data-testid={`detalle-linea-mobile-${i}`}>
              <div className="font-semibold text-sm text-[#15241d]">{l.producto_nombre || "—"}</div>
              <div className="text-[11px] text-[#5c7368]">
                {l.codigo_interno || ""}{l.laboratorio ? ` · ${l.laboratorio}` : ""}
                {l.presentacion ? ` · ${l.presentacion}` : ""}
              </div>
              <div className="flex items-center justify-between rounded bg-[#e9f2ee] border border-[#b2d0c2] px-2 py-1">
                <span className="text-[10px] uppercase tracking-wider text-[#205c45]">Cantidad</span>
                <span className="font-bold tabular-nums text-[#15241d]">
                  {l.cantidad} <span className="text-[11px] font-normal">{l.unidad_venta || ""}</span>
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[11px]">
                <div><div className="text-[10px] uppercase text-[#5c7368]">Lote</div><div className="font-mono">{l.numero_lote || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-[#5c7368]">Elaborac.</div><div>{l.fecha_elaboracion || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-[#5c7368]">Vence</div><div>{l.fecha_vencimiento || "—"}</div></div>
              </div>
              {l.observacion && <div className="text-[11px] text-[#8a5500]">Obs: {l.observacion}</div>}
            </div>
          ))}
          {(item.lineas || []).length === 0 && (
            <div className="text-center py-6 text-xs text-[#8ba397]">Sin productos en este despacho.</div>
          )}
        </div>

        <div className="hidden md:block border border-[#d1dbd6] rounded-md overflow-auto max-h-[50vh]">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Producto</th><th>Presentación</th><th>Lote</th><th>Elaborac.</th><th>Vence</th>
                <th className="text-right">Cantidad</th><th>Observación</th>
              </tr>
            </thead>
            <tbody>
              {(item.lineas || []).map((l, i) => (
                <tr key={i} data-testid={`detalle-linea-${i}`}>
                  <td className="text-xs">
                    <div className="font-semibold">{l.producto_nombre || "—"}</div>
                    <div className="text-[10px] text-[#5c7368]">{l.codigo_interno || ""} {l.laboratorio ? `· ${l.laboratorio}` : ""}</div>
                  </td>
                  <td className="text-xs">{l.presentacion || "—"}</td>
                  <td className="text-xs font-mono">{l.numero_lote || "—"}</td>
                  <td className="text-xs">{l.fecha_elaboracion || "—"}</td>
                  <td className="text-xs">{l.fecha_vencimiento || "—"}</td>
                  <td className="text-right tabular-nums text-xs font-semibold">
                    {l.cantidad} <span className="text-[10px] text-[#5c7368]">{l.unidad_venta || ""}</span>
                  </td>
                  <td className="text-xs">{l.observacion || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(item.historial || []).length > 0 && (
          <div className="text-[11px] text-[#5c7368]">
            {item.historial.map((h, i) => (
              <div key={i}>• {h.estado} — {(h.at || "").slice(0, 16).replace("T", " ")} {h.por ? `· ${h.por}` : ""} {h.detalle ? `· ${h.detalle}` : ""}</div>
            ))}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmpacarDialog({ item, onClose, onSaved }) {
  const [cartones, setCartones] = useState(item.cartones || 1);
  const [obs, setObs] = useState(item.observaciones_bodega || "");
  const [preparadas, setPreparadas] = useState(
    () => new Set((item.lineas || []).map((l, i) => (l.preparado ? i : null)).filter(i => i !== null)));
  const [saving, setSaving] = useState(false);

  const toggle = (i) => setPreparadas(prev => {
    const n = new Set(prev);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  const guardar = async () => {
    const n = parseInt(cartones, 10);
    if (!n || n < 1) return toast.error("Indique la cantidad de cartones (mínimo 1)");
    setSaving(true);
    try {
      await api.patch(`/despacho/${item.id}/empacar`, {
        cartones: n, observaciones_bodega: obs || null, lineas_preparadas: Array.from(preparadas),
      });
      toast.success(`${item.numero} empacado · ${n} cartón(es)`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Empacar {item.numero} · {item.cliente_nombre}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Cantidad de cartones / bultos *</Label>
            <Input type="number" min="1" value={cartones} onChange={(e) => setCartones(e.target.value)}
              className="text-lg font-semibold tabular-nums" data-testid="empacar-cartones" />
            <div className="text-[11px] text-[#5c7368] mt-1">Este número se usa en la guía de remisión.</div>
          </div>
          <div>
            <Label>Notas de bodega</Label>
            <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3} data-testid="empacar-obs" />
          </div>
        </div>
        <div className="space-y-2 md:hidden max-h-72 overflow-y-auto" data-testid="empacar-lineas-mobile">
          {(item.lineas || []).map((l, i) => (
            <label key={i} className={`flex items-start gap-2 rounded-lg border p-3 ${preparadas.has(i) ? "border-[#b2d0c2] bg-[#e9f2ee]" : "border-[#d1dbd6] bg-white"}`}>
              <input type="checkbox" className="mt-1 h-5 w-5" checked={preparadas.has(i)} onChange={() => toggle(i)}
                data-testid={`empacar-check-mobile-${i}`} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-[#15241d]">{l.producto_nombre}</div>
                <div className="text-[11px] text-[#5c7368]">
                  Lote {l.numero_lote || "—"}{l.fecha_vencimiento ? ` · Vence ${l.fecha_vencimiento}` : ""}
                </div>
                <div className="text-sm font-bold tabular-nums mt-1">{l.cantidad} <span className="text-[11px] font-normal">{l.unidad_venta || ""}</span></div>
              </div>
            </label>
          ))}
        </div>

        <div className="hidden md:block border border-[#d1dbd6] rounded-md max-h-64 overflow-auto">
          <table className="erp-table">
            <thead><tr><th className="w-10">✓</th><th>Producto</th><th>Lote</th><th>Vence</th><th className="text-right">Cantidad</th></tr></thead>
            <tbody>
              {(item.lineas || []).map((l, i) => (
                <tr key={i} className={preparadas.has(i) ? "bg-[#e9f2ee]" : ""}>
                  <td>
                    <input type="checkbox" checked={preparadas.has(i)} onChange={() => toggle(i)}
                      data-testid={`empacar-check-${i}`} />
                  </td>
                  <td className="text-xs">{l.producto_nombre}</td>
                  <td className="text-xs font-mono">{l.numero_lote || "—"}</td>
                  <td className="text-xs">{l.fecha_vencimiento || "—"}</td>
                  <td className="text-right tabular-nums text-xs">{l.cantidad} {l.unidad_venta || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-[#205c45]" data-testid="empacar-guardar">
            <PackageCheck size={15} className="mr-2" />{saving ? "Guardando…" : "Marcar empacado"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DespacharDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    fecha_despacho: item.fecha_despacho || new Date().toISOString().slice(0, 10),
    transportista: item.transportista || "",
    placa: item.placa || "",
    guia_remision_numero: item.guia_remision_numero || "",
    notas_despacho: item.notas_despacho || "",
  });
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    if (!form.fecha_despacho) return toast.error("Indique la fecha de despacho");
    setSaving(true);
    try {
      await api.patch(`/despacho/${item.id}/despachar`, form);
      toast.success(`${item.numero} despachado el ${form.fecha_despacho}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Despachar {item.numero}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-[#f7f9f8] border border-[#d1dbd6] rounded p-2">
            {item.cliente_nombre} · <strong>{item.cartones}</strong> cartón(es) · {item.documento_numero}
          </div>
          <div>
            <Label>Fecha de despacho *</Label>
            <Input type="date" value={form.fecha_despacho} onChange={(e) => setForm({ ...form, fecha_despacho: e.target.value })} data-testid="despachar-fecha" />
          </div>
          <div><Label>Transportista</Label><Input value={form.transportista} onChange={(e) => setForm({ ...form, transportista: e.target.value })} data-testid="despachar-transportista" /></div>
          <div><Label>Placa</Label><Input value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value })} data-testid="despachar-placa" /></div>
          <div><Label>N° guía de remisión (opcional)</Label><Input value={form.guia_remision_numero} onChange={(e) => setForm({ ...form, guia_remision_numero: e.target.value })} data-testid="despachar-guia" /></div>
          <div>
            <Label>Notas del despacho</Label>
            <Textarea rows={2} value={form.notas_despacho} onChange={(e) => setForm({ ...form, notas_despacho: e.target.value })}
              placeholder="Ej: recibido por Sra. Pérez, entrega parcial, 1 cartón con gel refrigerante…" data-testid="despachar-notas" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} className="bg-[#205c45]" data-testid="despachar-guardar">
            <Truck size={15} className="mr-2" />{saving ? "Guardando…" : "Confirmar despacho"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Genera la guía de remisión SRI precargada con los datos del despacho. */
function GuiaDialog({ item, onClose, onSaved }) {
  const hoy = new Date().toISOString().slice(0, 10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transportista, setTransportista] = useState({
    razon_social_transportista: "", tipo_identificacion_transportista: "ruc",
    ruc_transportista: "", placa: "", dir_partida: "",
  });
  const [fechas, setFechas] = useState({ fecha: hoy, fecha_ini_transporte: hoy, fecha_fin_transporte: hoy });
  const [destinatario, setDestinatario] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        if (item.documento_tipo !== "factura") {
          setLoading(false);
          return;
        }
        const { data } = await api.get(`/invoicing/facturas/${item.documento_id}/guia-preview`);
        setTransportista(t => ({ ...t, ...data.transportista }));
        setFechas(f => ({ ...f, ...data.fechas }));
        setDestinatario({ ...data.destinatario });
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [item]);

  const crear = async () => {
    if (!destinatario) return;
    if (!transportista.razon_social_transportista?.trim()) return toast.error("Indique la razón social del transportista");
    if (!transportista.ruc_transportista?.trim()) return toast.error("Indique la identificación del transportista");
    if (!transportista.placa?.trim()) return toast.error("Indique la placa del vehículo");
    if (!transportista.dir_partida?.trim()) return toast.error("Indique la dirección de partida");
    const cartones = parseInt(item.cartones, 10) || 1;
    setSaving(true);
    try {
      const payload = {
        fecha: fechas.fecha,
        dir_partida: transportista.dir_partida,
        razon_social_transportista: transportista.razon_social_transportista,
        tipo_identificacion_transportista: transportista.tipo_identificacion_transportista || "ruc",
        ruc_transportista: transportista.ruc_transportista,
        placa: transportista.placa,
        fecha_ini_transporte: fechas.fecha_ini_transporte,
        fecha_fin_transporte: fechas.fecha_fin_transporte,
        destinatarios: [{
          cliente_id: destinatario.cliente_id,
          identificacion: destinatario.identificacion,
          razon_social: destinatario.razon_social,
          direccion: destinatario.direccion,
          email: destinatario.email || null,
          telefono: destinatario.telefono || null,
          motivo_traslado: `${destinatario.motivo_traslado || "VENTA"} · BULTOS: ${cartones} CARTON${cartones !== 1 ? "ES" : ""}`,
          ruta: destinatario.ruta || null,
          cod_doc_sustento: destinatario.cod_doc_sustento || "01",
          num_doc_sustento: destinatario.num_doc_sustento || "",
          num_autorizacion_doc_sustento: destinatario.num_autorizacion_doc_sustento || null,
          fecha_emision_doc_sustento: destinatario.fecha_emision_doc_sustento,
          detalles: destinatario.detalles,
        }],
      };
      const { data: g } = await api.post("/invoicing/guias-remision", payload);
      await api.patch(`/despacho/${item.id}/despachar`, {
        transportista: transportista.razon_social_transportista,
        placa: transportista.placa,
        guia_remision_numero: g.numero,
      });
      toast.success(`Guía ${g.numero} creada (borrador) · ${cartones} cartón(es). Fírmela en Complementos SRI → Guías.`, { duration: 9000 });
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Guía de remisión · {item.numero}</DialogTitle></DialogHeader>
        {item.documento_tipo !== "factura" ? (
          <div className="text-sm text-[#7a2514] bg-[#fdecea] border border-[#d99584] rounded p-3" data-testid="guia-np-warning">
            Este despacho proviene de una <strong>nota de pedido</strong>. La guía de remisión electrónica requiere una factura
            de sustento: facture la nota de pedido {item.documento_numero} y luego genere la guía desde el despacho de esa factura.
          </div>
        ) : loading ? (
          <div className="py-8 text-center text-sm text-[#8ba397]">Cargando datos de la factura…</div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs bg-[#e9f2ee] border border-[#b2d0c2] rounded p-2">
              Sustento: Factura <strong>{destinatario?.num_doc_sustento}</strong> · Destinatario: <strong>{destinatario?.razon_social}</strong> ·
              Bultos: <strong>{item.cartones} cartón(es)</strong>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Transportista *</Label><Input value={transportista.razon_social_transportista} onChange={(e) => setTransportista({ ...transportista, razon_social_transportista: e.target.value })} data-testid="guia-transportista" /></div>
              <div><Label>RUC / Cédula transportista *</Label><Input value={transportista.ruc_transportista} onChange={(e) => setTransportista({ ...transportista, ruc_transportista: e.target.value })} data-testid="guia-ruc" /></div>
              <div><Label>Placa *</Label><Input value={transportista.placa} onChange={(e) => setTransportista({ ...transportista, placa: e.target.value })} data-testid="guia-placa" /></div>
              <div><Label>Dirección de partida *</Label><Input value={transportista.dir_partida} onChange={(e) => setTransportista({ ...transportista, dir_partida: e.target.value })} data-testid="guia-partida" /></div>
              <div><Label>Fecha emisión</Label><Input type="date" value={fechas.fecha} onChange={(e) => setFechas({ ...fechas, fecha: e.target.value })} /></div>
              <div><Label>Inicio transporte</Label><Input type="date" value={fechas.fecha_ini_transporte} onChange={(e) => setFechas({ ...fechas, fecha_ini_transporte: e.target.value })} /></div>
              <div><Label>Fin transporte</Label><Input type="date" value={fechas.fecha_fin_transporte} onChange={(e) => setFechas({ ...fechas, fecha_fin_transporte: e.target.value })} /></div>
              <div><Label>Dirección destinatario</Label><Input value={destinatario?.direccion || ""} onChange={(e) => setDestinatario({ ...destinatario, direccion: e.target.value })} data-testid="guia-direccion" /></div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          {item.documento_tipo === "factura" && !loading && (
            <Button onClick={crear} disabled={saving} className="bg-[#205c45]" data-testid="guia-crear">
              <UserCog size={15} className="mr-2" />{saving ? "Creando…" : "Crear guía y despachar"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
